import { FlexBox } from '@dixa/lib-shared-habla';
import { Message } from 'lib-shared-legacy-types';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { getMessageSanitizationChannel } from 'app/utils/htmlSanitizer';
import { getMessageMarkup } from 'app/utils/messages';
import { TranslateTextChangeData } from 'components/translateTextDropdownSelectionList';
import { TranslationBadge } from 'components/translationBadge';

import { Message as MessageComp, MessageProps } from '../message';

export type TextMessageProps = {
  /** The original message */
  message: Message;
  /** Internal link catch. For blocking page load and use React-Router instead */
  catchInternalLinks?: boolean;
  /**
   * The translation object containing all the necessary message translation
   * information.
   */
  translation?: TranslateTextChangeData;
} & MessageProps;

const TextMessage = ({
  message,
  catchInternalLinks,
  translation,
  nameMenu,
  ...otherProps
}: TextMessageProps): JSX.Element => {
  const navigate = useNavigate();

  const sanitizationChannel = getMessageSanitizationChannel(message);

  const handleCatchInternalLinks = (e: React.MouseEvent) => {
    if (e.target instanceof HTMLAnchorElement) {
      const origin = window.location.origin;
      const href = e.target.href;
      if (href.indexOf(origin) === 0) {
        e.preventDefault();
        const relativeUrl = href.replace(origin, '');
        navigate(relativeUrl);
      }
    }
  };

  const preparedMarkup = useMemo(() => {
    if (translation?.status === 'fulfilled' && translation) {
      return getMessageMarkup({ ...message, text: translation.translatedText });
    }

    return getMessageMarkup(message);
  }, [message, translation]);

  const nameMenuContent = useMemo(() => {
    return (
      <FlexBox gap="200" alignItems="center">
        {nameMenu}

        {translation?.status && (
          <TranslationBadge
            translation={translation}
            originalLanguageCode={message.language ?? undefined}
            errorMessage="There was an error translating this message. Please try again."
          />
        )}
      </FlexBox>
    );
  }, [message.language, nameMenu, translation]);

  return (
    <MessageComp
      {...otherProps}
      onClick={catchInternalLinks ? handleCatchInternalLinks : undefined}
      markup={preparedMarkup}
      nameMenu={nameMenuContent}
      channel={sanitizationChannel}
    />
  );
};

export default TextMessage;
